import {
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Table,
    Tbody,
    Text,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { TbPlus, TbTrash } from 'react-icons/tb';
import {
    SettingsNumberInput,
    SettingsTd,
    SettingsTh,
} from '../../../../components/settings/primitives';
import { SETTINGS_BORDER_COLOR } from '../../../../components/settings/primitives/constants';
import { formatTierRange, newRebateTier, type RebateTier } from './oemDraft';

export interface OemRebateTableProps {
    tiers: RebateTier[];
    onChange: (next: RebateTier[]) => void;
    isDisabled?: boolean;
}

/**
 * Rebate tiers by ordering threshold: what the OEM pays back once a share of
 * parts is ordered through a qualifying marketplace. The top tier leaves Max
 * blank, which stores as open-ended, so "85%+" needs no separate switch.
 *
 * A contained table whose height follows its rows, matching the other tables in
 * these flows, with Add row under it rather than a floating control.
 */
const OemRebateTable = ({ tiers, onChange, isDisabled = false }: OemRebateTableProps) => {
    const updateTier = (id: string, changes: Partial<RebateTier>) => {
        onChange(tiers.map((tier) => (tier.id === id ? { ...tier, ...changes } : tier)));
    };

    return (
        <Flex direction="column" minH={0} maxH="100%">
            <Box
                minH={0}
                bg="white"
                borderWidth="1px"
                borderColor={SETTINGS_BORDER_COLOR}
                borderRadius="lg"
                overflow="hidden"
            >
                <Box
                    minH={0}
                    overflow="auto"
                    sx={{
                        '& thead': { zIndex: 2 },
                        '& th': {
                            bg: 'gray.50',
                            borderBottomWidth: '1px',
                            borderBottomColor: 'var(--chakra-colors-gray-200)',
                        },
                        '& tbody tr:last-of-type td': { borderBottomWidth: 0 },
                        '& th:first-of-type, & td:first-of-type': { pl: 5 },
                        '& th:last-of-type, & td:last-of-type': { pr: 5 },
                        '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#cbd5e0',
                            borderRadius: '4px',
                        },
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e0 transparent',
                    }}
                >
                    <Table variant="simple" size="sm" w="100%" sx={{ tableLayout: 'fixed' }}>
                        <Thead position="sticky" top={0}>
                            <Tr bg="gray.50">
                                <SettingsTh>ORDERING THRESHOLD</SettingsTh>
                                <SettingsTh w="140px">REBATE</SettingsTh>
                                <SettingsTh w="70px" />
                            </Tr>
                        </Thead>
                        <Tbody>
                            {tiers.length === 0 ? (
                                <Tr>
                                    <SettingsTd colSpan={3} py={8} textAlign="center">
                                        <Text fontSize="sm" color="gray.500">
                                            No rebate tiers yet
                                        </Text>
                                    </SettingsTd>
                                </Tr>
                            ) : (
                                tiers.map((tier) => (
                                    <Tr key={tier.id} _hover={{ bg: 'gray.25' }}>
                                        <SettingsTd>
                                            <HStack spacing={2} align="center">
                                                <SettingsNumberInput
                                                    value={tier.min}
                                                    onChange={(min) => updateTier(tier.id, { min })}
                                                    min={0}
                                                    max={100}
                                                    allowDecimals
                                                    width="76px"
                                                    unit="%"
                                                    placeholder="Min"
                                                    isDisabled={isDisabled}
                                                    ariaLabel="Minimum ordering threshold"
                                                />
                                                <Text fontSize="xs" color="gray.500">
                                                    to
                                                </Text>
                                                <SettingsNumberInput
                                                    value={tier.max}
                                                    onChange={(max) => updateTier(tier.id, { max })}
                                                    min={0}
                                                    max={100}
                                                    allowDecimals
                                                    width="76px"
                                                    unit="%"
                                                    placeholder="Max"
                                                    isDisabled={isDisabled}
                                                    ariaLabel="Maximum ordering threshold"
                                                />
                                                <Text fontSize="xs" color="gray.400" noOfLines={1}>
                                                    {formatTierRange(tier)}
                                                </Text>
                                            </HStack>
                                        </SettingsTd>
                                        <SettingsTd>
                                            <SettingsNumberInput
                                                value={tier.rebate}
                                                onChange={(rebate) => updateTier(tier.id, { rebate })}
                                                min={0}
                                                max={100}
                                                allowDecimals
                                                width="76px"
                                                unit="%"
                                                placeholder="0"
                                                isDisabled={isDisabled}
                                                ariaLabel="Rebate"
                                            />
                                        </SettingsTd>
                                        <SettingsTd textAlign="center">
                                            <Flex justify="center">
                                                <IconButton
                                                    aria-label="Remove tier"
                                                    icon={
                                                        <Icon
                                                            as={TbTrash as React.ElementType}
                                                            boxSize={4}
                                                        />
                                                    }
                                                    size="xs"
                                                    variant="ghost"
                                                    colorScheme="red"
                                                    isDisabled={isDisabled}
                                                    onClick={() =>
                                                        onChange(
                                                            tiers.filter(
                                                                (candidate) =>
                                                                    candidate.id !== tier.id,
                                                            ),
                                                        )
                                                    }
                                                />
                                            </Flex>
                                        </SettingsTd>
                                    </Tr>
                                ))
                            )}
                        </Tbody>
                    </Table>
                </Box>
            </Box>

            <Box pt={2} flexShrink={0}>
                <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    leftIcon={<Icon as={TbPlus as React.ElementType} boxSize={3.5} />}
                    isDisabled={isDisabled}
                    onClick={() => onChange([...tiers, newRebateTier()])}
                >
                    Add tier
                </Button>
            </Box>
        </Flex>
    );
};

export default OemRebateTable;
